import { useMemo } from 'react'
import { useQuery } from '@apollo/client'

import type { RequestTrendStatus, TrendBase, TrendRelatedTerritory, TrendRelevance, TrendStatus } from 'entities/trend'
import type { GetTrendingsByCategoryQuery } from 'generated/graphql'

import { GET_TRENDINGS_BY_CATEGORY_QUERY } from './operations'

export const useTrends = () => {
  const { data, loading, error } = useQuery<GetTrendingsByCategoryQuery>(GET_TRENDINGS_BY_CATEGORY_QUERY)

  const requestTrendStatus: RequestTrendStatus = data?.getTrendingHome?.status

  const relatedTerritory = useMemo<TrendRelatedTerritory>(() => {
    const relatedTerritoryData = data?.getTrendingHome?.relatedTerritory

    if (!relatedTerritoryData?.territoryId) {
      return null
    }

    return {
      territoryId: relatedTerritoryData?.territoryId,
      name: relatedTerritoryData?.name,
    }
  }, [data?.getTrendingHome?.relatedTerritory])

  const trends = useMemo<TrendBase[]>(() => {
    const trendsData = Array.from(data?.getTrendingHome?.data || [])
    const parsedTrends = trendsData.map(trend => {
      const relevanceData = JSON.parse(trend?.relevance) || []
      const parsedRelevance: TrendRelevance[] = Array.from(relevanceData).map((relevance: any) => ({
        relevance: relevance?.relevance,
        postCount: relevance?.post_count,
        publishedAt: relevance?.published_at,
      }))

      return {
        id: trend?.id,
        name: trend?.name,
        status: trend?.status as TrendStatus,
        query: trend?.query,
        filtersObject: trend?.filtersObject,
        totalPosts: trend?.totalPosts,
        relevance: parsedRelevance,
      }
    })

    return parsedTrends
  }, [data?.getTrendingHome?.data])

  const isLoading = loading

  const memoizedValues = useMemo(
    () => ({
      status: requestTrendStatus,
      relatedTerritory,
      trends,
      isLoading,
      error,
    }),
    [relatedTerritory, trends, isLoading, error, requestTrendStatus]
  )

  return memoizedValues
}
